import { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchProofTrace,
  normalizeProofTraceRequest,
  validateProofTraceRequest,
  type ProofTraceRequest,
  type ProofTraceResult,
} from "../../api/proofTrace";

interface ProofTraceLoadState {
  key: string | null;
  data: ProofTraceResult | null;
  error: Error | null;
  loading: boolean;
}

export interface ProofTraceState {
  data: ProofTraceResult | null;
  error: Error | null;
  isLoading: boolean;
  refresh: () => void;
}

export function proofTraceRequestKey(request: ProofTraceRequest): string {
  validateProofTraceRequest(request);
  const normalizedRequest = normalizeProofTraceRequest(request);
  return JSON.stringify([
    normalizedRequest.project_id,
    normalizedRequest.root_ref.entity_kind,
    normalizedRequest.root_ref.id,
    normalizedRequest.as_of ?? null,
    normalizedRequest.recorded_as_of ?? null,
    normalizedRequest.snapshot_token ?? null,
    normalizedRequest.max_depth ?? null,
    normalizedRequest.max_records ?? null,
    normalizedRequest.max_paths ?? null,
    normalizedRequest.max_path_steps ?? null,
  ]);
}

function aborted(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/**
 * Loads one exact proof request. Data is keyed to the complete request, so a
 * project, root, temporal instant, or limit change cannot flash a stale proof.
 */
export function useProofTrace(request: ProofTraceRequest | null): ProofTraceState {
  const key = useMemo(() => request ? proofTraceRequestKey(request) : null, [
    request?.project_id,
    request?.root_ref.entity_kind,
    request?.root_ref.id,
    request?.as_of,
    request?.recorded_as_of,
    request?.snapshot_token,
    request?.max_depth,
    request?.max_records,
    request?.max_paths,
    request?.max_path_steps,
  ]);
  const [reload, setReload] = useState(0);
  const [state, setState] = useState<ProofTraceLoadState>({
    key: null,
    data: null,
    error: null,
    loading: false,
  });

  useEffect(() => {
    if (!request || !key) {
      setState({ key: null, data: null, error: null, loading: false });
      return;
    }
    const controller = new AbortController();
    let active = true;
    setState({ key, data: null, error: null, loading: true });
    void fetchProofTrace(request, { signal: controller.signal }).then(
      (envelope) => {
        if (active) setState({ key, data: envelope.data, error: null, loading: false });
      },
      (error: unknown) => {
        if (!active || aborted(error)) return;
        setState({
          key,
          data: null,
          error: error instanceof Error ? error : new Error("Could not load the proof trace."),
          loading: false,
        });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, reload]);

  const refresh = useCallback(() => {
    if (!key) return;
    setState({ key, data: null, error: null, loading: true });
    setReload((value) => value + 1);
  }, [key]);

  if (state.key !== key) {
    return { data: null, error: null, isLoading: key !== null, refresh };
  }
  return { data: state.data, error: state.error, isLoading: state.loading, refresh };
}
